import { withNamespaces } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { withRouter, useHistory, Link } from "react-router-dom";
import { Container, Col, Row, Card, CardBody, Alert } from 'reactstrap';
import { connect } from 'react-redux';
import axios from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../../../constants';
import QuestionnaireDesignerService from 'src/modules/data-protection/apis/QuestionnaireDesignerService';
import { TwitterWizard, TwitterWizardNavItem } from 'src/modules/data-protection/components/TwitterWizard/index.jsx';
import Navigator from 'src/modules/data-protection/components/navigator/index.jsx';
import { 
    SimpleQuestionnaire,
    AnswerSheet,
    helpers
} from "@smartintegrity/questionnaire";
import { mapAnswersToSave, mapQuestions } from "src/modules/data-protection/helpers/questionnaire";
import CommonService from 'src/modules/data-protection/apis/CommonService';
import RequesterDetails from './components/RequesterDetails';

const QuestionsLoadingPlaceholder = () => {
    return (
        <div>
            <Row className="mb-4">
                <Col lg="12">
                    <p style={{width: '90%'}} className={`dt-field dt-skeleton`}></p>
                </Col>
                <Col lg="6">
                    <Row>
                        <Col lg="6">
                            <p style={{width: '100%'}} className={`dt-field dt-skeleton`}></p>
                        </Col>
                        <Col lg="6">
                            <p style={{width: '100%'}} className={`dt-field dt-skeleton`}></p>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg="6">
                            <p style={{width: '100%'}} className={`dt-field dt-skeleton`}></p>
                        </Col>
                        <Col lg="6">
                            <p style={{width: '100%'}} className={`dt-field dt-skeleton`}></p>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row className="mb-4">
                <Col lg="12">
                    <p style={{width: '90%'}} className={`dt-field dt-skeleton`}></p>
                </Col>
                <Col lg="6">
                    <Row>
                        <Col lg="12">
                            <p style={{width: '100%'}} className={`dt-field dt-skeleton`}></p>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row className="mb-4">
                <Col lg="12">
                    <p style={{width: '90%'}} className={`dt-field dt-skeleton`}></p>
                </Col>
                <Col lg="6">
                    <Row>
                        <Col lg="12">
                            <p style={{width: '100%'}} className={`dt-field dt-skeleton`}></p>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row className="mb-4">
                <Col lg="12">
                    <p style={{width: '90%'}} className={`dt-field dt-skeleton`}></p>
                </Col>
                <Col lg="6">
                    <Row>
                        <Col lg="12">
                            <p style={{width: '100%'}} className={`dt-field dt-skeleton`}></p>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </div>
    );
}

const DataSubjectRequestCreate = ({
    t,
    Organization,
    token,
    user,
}) => {
    const history = useHistory();

    const [activeTab, setActiveTab] = useState("fill-questions-step");
    const [filledAnswers, setFilledAnswers] = useState([]);
    const [requesterDetails, setRequesterDetails] = useState(null);

    const questionsQuery = useQuery({
        queryKey: ['gdpr-fetch-data-subject-request-questions'],
        queryFn: async () => {
            const service = QuestionnaireDesignerService.getInstance();

            const response = await service.questions(
                'data_subject_request', 
                Organization.id, 
                {}
            );

            const mappedQuestions = [
                {
                    id: -1,
                    title: "Choose the channel of information",
                    type: "select",
                    sort_index: 0,
                    is_mandatory: true,
                    options: [
                        { title: "Web", id: "Web" },
                        { title: "Email", id: "Email" },
                        { title: "Letter", id: "Letter" },
                        { title: "Phone", id: "Phone" },
                        { title: "Meeting", id: "Meeting" },
                        { title: "Live Chat", id: "Live Chat" },
                        { title: "Other", id: "Other" }
                    ]
                },
                ...mapQuestions(response || [])
            ];

            return {
                mappedQuestions: helpers.makeQuestionsReadyForQuestionnaire(
                    mappedQuestions,
                    false
                ),
                rawQuestions: mappedQuestions
            }
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t('An error occurred while fetching questions.'), {
                type: 'error'
            });
        }
    });

    const fetchLegalEntitiesQuery = useQuery({
        queryKey: [
            "gdpr-fetch-organization-legal-entities",
            Organization.id
        ],
        queryFn: async () => {
            const service = CommonService.getInstance();
            return await service.legalEntities(Organization?.id);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);

            toast(t("An error occurred while fetching legal entities."), {
                type: "error"
            });
        }
    });

    const fetchCountriesQuery = useQuery({
        queryKey: ["gdpr-report-countries"],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.countries();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === "development") console.error(error);

            toast(t("An error occurred while fetching countries."), {
                type: "error"
            });
        },
    });

    const submitQuestionnaireMutation = useMutation({
        mutationFn: async (payload) => {
            return await axios.post(`${API_BASE_URL}/report.create`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
        },
        onSuccess() {
            toast(t("Report Submitted"), {
                type: 'success'
            });
        },
        onError(error) {
            if (process.env.NODE_ENV === 'development') console.error(error)
            toast(t('Error!'), {
                type: "error"
            })
        }
    });

    const sendQuestionnaireToSave = (details) => {
        const sourceQuestionAnswer = filledAnswers.find((ans) => ans.question === -1);
        const followUpDetails = details || requesterDetails;
        const requesterType = followUpDetails?.requester_type || "company";

        const payload = {
            questionAnswers: mapAnswersToSave(
                questionsQuery.data?.rawQuestions,
                filledAnswers.filter((ans) => ans.question !== -1)
            ),
            questionnaireType: 'data_subject_request',
            source: sourceQuestionAnswer ? (sourceQuestionAnswer.answer[0]?.value)?.toLowerCase() : null,
            requester_type: requesterType,
            phone: followUpDetails?.phone || null,
            email: followUpDetails?.email || null
        };

        if (requesterType === "company") {
            payload.organization_name = followUpDetails?.organization_name || null;
            payload.organization_website = followUpDetails?.organization_website || null;
            payload.email_domain_mismatch = !!followUpDetails?.email_domain_mismatch;

            if (followUpDetails?.email_domain_proof) {
                payload.email_domain_proof = followUpDetails.email_domain_proof;
                payload.files = [followUpDetails.email_domain_proof];
            }
        }

        submitQuestionnaireMutation.mutate(payload);
    }

    const extraData = useMemo(() => {
        return {
            legalEntities: {
                isFetching: fetchLegalEntitiesQuery.isFetching,
                data: fetchLegalEntitiesQuery.data || []
            },
            countries: {
                isFetching: fetchCountriesQuery.isFetching,
                data: (fetchCountriesQuery.data || []).map((country) => {
                    return {
                        ...country,
                        title: country.name
                    }
                })
            }
        }
    }, [
        fetchLegalEntitiesQuery.data,
        fetchLegalEntitiesQuery.isFetching,
        fetchCountriesQuery.data,
        fetchCountriesQuery.isFetching
    ]);

    const settings = useMemo(() => {
        let submitButtonSettings = {
            enable: true,
            title: t('Next')
        }

        return {
            panelSettings: {
                showNumber: true,
                showProgressBar: false,
                prefix: ''
            },
            buttonsSettings: {
                submit: {
                    display: true,
                    ...submitButtonSettings
                },
                back: {
                    display: false
                }
            },
            fileUploaderOptions: {
                deleteRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId,
                        file
                    }) => {
                        return `${API_BASE_URL}/attachment/${file.id}/delete`
                    }
                },
                uploadRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId
                    }) => {
                        return `${API_BASE_URL}/report.uploadAttachment`
                    }
                }
            },
            questions: {
                showNumber: true
            }
        }
    }, [
        token,
        t
    ]);

    const answerSheetSettings = useMemo(() => {
        return {
            buttons: {
                back: {
                    enabled: true
                },
                confirm: {
                    enabled: true,
                    title: t('Next')
                }
            },
            fileDownloaderOptions: {
                downloadRequestOptions: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    payload: {},
                    requestUrl: ({
                        questionId,
                        attachmentId
                    }) => {
                        return `${API_BASE_URL}/report/attachment/${attachmentId}/download`
                    }
                }
            }
        }
    }, [
        t,
        token
    ])

    const questionnaireDefaultValues = useMemo(() => {
        const questions = questionsQuery.data?.rawQuestions || []; 
        if(questions.length){
            return helpers.makePreviousAnswersReadyForQuestionnaire(
                questions,
                filledAnswers.map((answer) => {
                    return {
                        ...(questions.find((question) => answer.question === question.id) || {}),
                        answers: answer.answer
                    }
                })
            )
        }

        return {}
    }, [
        questionsQuery.data?.rawQuestions,
        filledAnswers
    ])

    const handleSubmitQuestionnaire = useCallback((payload) => {
        setFilledAnswers(payload)
    }, []);

    useEffect(() => {
        if (filledAnswers.length > 0) {
            setActiveTab('review-filled-answers');
        }
    }, [
        filledAnswers
    ]);

    return (
        <div className='page-content'>
            <Container fluid>
                <Row className="mb-4">
                    <Col sm="12">
                        <Navigator
                            backButtonOptions={() => {
                                return {
                                    enable: true,
                                    onClick: () => history.goBack(),
                                    title: t("Back"),
                                    icon: <i className="ri-arrow-left-fill"></i>,
                                };
                            }}
                            breadCrumbs={[
                                { title: "SIP", link: "/" },
                                {
                                    title: t("Data Subject Request"),
                                    link: `/admin/data-protection/data-subject-request`,
                                }
                            ]}
                        />
                    </Col>
                </Row>

                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardBody>
                                <TwitterWizard activeTab={activeTab} >
                                    <TwitterWizardNavItem uniqueKey={'fill-questions-step'}>
                                        <span className="step-number">01</span>
                                        <span className="step-title">
                                            {t("Report")}
                                        </span>
                                    </TwitterWizardNavItem>

                                    <TwitterWizardNavItem uniqueKey={'review-filled-answers'}>
                                        <span className="step-number">02</span>
                                        <span className="step-title">
                                            {t("Review your report")}
                                        </span>
                                    </TwitterWizardNavItem>

                                    <TwitterWizardNavItem uniqueKey={'follow-up-step'}>
                                        <span className="step-number">03</span>
                                        <span className="step-title">
                                            {t("Follow-Up")}
                                        </span>
                                    </TwitterWizardNavItem>

                                    <TwitterWizardNavItem uniqueKey={'finalize-report'}>
                                        <span className="step-number">04</span>
                                        <span className="step-title">
                                            {t("Finalize")}
                                        </span>
                                    </TwitterWizardNavItem>
                                </TwitterWizard>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col sm={12}>
                        <Card>
                            <CardBody className='smartintegrity__questionnaire__tabs_progress'>
                                {(() => {
                                    if(questionsQuery.isFetching){
                                        return (
                                            <QuestionsLoadingPlaceholder />
                                        )
                                    }

                                    if(activeTab === 'fill-questions-step'){
                                        return (
                                            <SimpleQuestionnaire
                                                questionnaireUniqueId='data_subject_request'
                                                className='gdpr'
                                                questions={questionsQuery.data?.mappedQuestions || []}
                                                defaultValues={questionnaireDefaultValues}
                                                settings={settings}
                                                onSubmit={handleSubmitQuestionnaire}
                                                extraData={extraData}
                                            />
                                        )
                                    }
                                    else if(activeTab === 'review-filled-answers'){
                                        return (
                                            <AnswerSheet 
                                                settings={ answerSheetSettings }
                                                questions={ questionsQuery.data?.mappedQuestions || [] }
                                                answers={filledAnswers}
                                                onConfirmed={() => {
                                                    setActiveTab('follow-up-step');
                                                }}
                                                onBackButtonClicked={() => {
                                                    setActiveTab('fill-questions-step');
                                                }}
                                                multiTab={false}
                                            />
                                        )
                                    }
                                    else if(activeTab === 'follow-up-step'){
                                        return (
                                            <RequesterDetails
                                                user={user}
                                                initialValues={requesterDetails}
                                                onBackButtonClicked={() => {
                                                    setActiveTab('review-filled-answers');
                                                }}
                                                onSubmit={(details) => {
                                                    setRequesterDetails(details);
                                                    setActiveTab('finalize-report');
                                                    sendQuestionnaireToSave(details);
                                                }}
                                            />
                                        )
                                    }
                                    else if(activeTab === 'finalize-report'){
                                        if(submitQuestionnaireMutation.isSuccess){
                                            return (
                                                <>
                                                    <Alert color="success"
                                                        className="alert-dismissible fade show"
                                                        role="alert">
                                                        <i className="mdi mdi-check-all me-2"></i>
                                                        <b>
                                                            {t("Report Submitted")}
                                                        </b>
                                                        <p>
                                                            {t(
                                                                "If you have any questions, please contact the technical support by sending an email to support@diss-co.tech."
                                                            )}
                                                        </p>
                                                        <p>
                                                            {t(
                                                                "If you receive error messages or have a technical questions, please make a screenshot of the error message or your view including the URL and send your request by email to support@diss-co.tech"
                                                            )}
                                                        </p>
                                                    </Alert>
                                                    <Link
                                                        className='btn btn-primary'
                                                        to={`/admin/data-protection/data-subject-request`}
                                                    >
                                                        {t("Back to Data Subject Request")}
                                                    </Link>
                                                </>
                                            )
                                        }
                                        else if(submitQuestionnaireMutation.isError){
                                            return (
                                                <Alert color="danger"
                                                    className="alert-dismissible fade show"
                                                    role="alert">
                                                    <i className="mdi mdi-check-all me-2"></i>
                                                    <b>
                                                        {t("Error!")}
                                                    </b>
                                                    <p>
                                                        {t("An error occurred while submitting the form")}
                                                    </p>
                                                </Alert>
                                            )
                                        }
                                        else{
                                            return (
                                                <p>
                                                    {t(
                                                        "Thank you for your patience while your request is being processed."
                                                    )}
                                                </p>
                                            )
                                        }
                                    }

                                    return null;
                                })()}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

const mapStatetoProps = (state) => {
    const { token, user } = state.Login;
    const { Organization } = state;

    return {
        token,
        user,
        Organization
    };
};

export default withNamespaces()(
    connect(mapStatetoProps)(
        withRouter(
            DataSubjectRequestCreate
        )
    )
);
